import React, { useState, useRef, useCallback } from "react";
import { apiPatch } from "../../services/api";
import ImageUploader from "./ImageUploader";
import "../styles/EditableBlock.css";
import { useNotification } from "../../contexts/NotificationContext";

// Word-подобный Rich Text редактор (компактная версия)
const WordLikeEditor = ({ value, onChange, placeholder, height = 150 }) => {
  const editorRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({});

  const execCommand = useCallback(
    (command, value = null) => {
      document.execCommand(command, false, value);
      editorRef.current?.focus();
      updateActiveFormats();
      if (onChange) {
        setTimeout(() => {
          onChange(editorRef.current?.innerHTML || "");
        }, 10);
      }
    },
    [onChange]
  );

  const updateActiveFormats = useCallback(() => {
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
    });
  }, []);

  const handleInput = useCallback(() => {
    if (onChange) {
      onChange(editorRef.current?.innerHTML || "");
    }
    updateActiveFormats();
  }, [onChange, updateActiveFormats]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.ctrlKey && e.key === "b") {
        e.preventDefault();
        execCommand("bold");
      }
      if (e.ctrlKey && e.key === "i") {
        e.preventDefault();
        execCommand("italic");
      }
      if (e.ctrlKey && e.key === "u") {
        e.preventDefault();
        execCommand("underline");
      }
    },
    [execCommand]
  );

  const applyColor = useCallback(
    (color) => {
      execCommand("foreColor", color);
    },
    [execCommand]
  );

  const applyBackgroundColor = useCallback(
    (color) => {
      execCommand("backColor", color);
    },
    [execCommand]
  );

  React.useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  return (
    <div className="compact-word-editor">
      {/* Компактная панель инструментов */}
      <div className="compact-toolbar">
        {/* Основное форматирование */}
        <div className="toolbar-group">
          <button
            type="button"
            className={`toolbar-btn ${activeFormats.bold ? "active" : ""}`}
            onClick={() => execCommand("bold")}
            title="Жирный (Ctrl+B)"
          >
            <strong>Ж</strong>
          </button>

          <button
            type="button"
            className={`toolbar-btn ${activeFormats.italic ? "active" : ""}`}
            onClick={() => execCommand("italic")}
            title="Курсив (Ctrl+I)"
          >
            <em>К</em>
          </button>

          <button
            type="button"
            className={`toolbar-btn ${activeFormats.underline ? "active" : ""}`}
            onClick={() => execCommand("underline")}
            title="Подчеркивание (Ctrl+U)"
          >
            <u>П</u>
          </button>
        </div>

        <div className="toolbar-separator"></div>

        {/* Быстрые цвета */}
        <div className="toolbar-group">
          {["#000000", "#ff0000", "#00aa00", "#0066cc", "#004D40"].map(
            (color) => (
              <button
                key={color}
                type="button"
                className="color-btn-small"
                style={{ backgroundColor: color }}
                onClick={() => applyColor(color)}
                title={`Цвет: ${color}`}
              >
                А
              </button>
            )
          )}
        </div>

        <div className="toolbar-separator"></div>

        {/* Выравнивание */}
        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand("justifyLeft")}
            title="По левому краю"
          >
            ⬅
          </button>

          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand("justifyCenter")}
            title="По центру"
          >
            ⬌
          </button>

          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand("justifyRight")}
            title="По правому краю"
          >
            ➡
          </button>
        </div>
      </div>

      {/* Редактор */}
      <div
        ref={editorRef}
        className="compact-editor-content"
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onMouseUp={updateActiveFormats}
        onKeyUp={updateActiveFormats}
        style={{ height: `${height}px` }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

// Компонент для Rich Text полей с переводами
const EnhancedTextInput = ({
  fieldName,
  label,
  required = false,
  blockContent,
  setBlockContent,
  currentLanguage,
  setCurrentLanguage,
  languages,
  height = 150,
}) => {
  const currentValue = blockContent[fieldName]?.[currentLanguage] || "";

  const handleChange = (value) => {
    setBlockContent((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        [currentLanguage]: value,
      },
    }));
  };

  return (
    <div className="form-group enhanced-text-group">
      <label>{label}:</label>

      <div className="language-tabs">
        {languages.map((lang) => (
          <button
            key={lang.code}
            type="button"
            className={`language-tab ${currentLanguage === lang.code ? "active" : ""
              }`}
            onClick={() => setCurrentLanguage(lang.code)}
          >
            {lang.name}
          </button>
        ))}
      </div>

      <WordLikeEditor
        value={currentValue}
        onChange={handleChange}
        placeholder={`Введите ${label.toLowerCase()} на ${languages.find((l) => l.code === currentLanguage)?.name
          }`}
        height={height}
      />

      <div className="translation-indicators">
        {languages.map((lang) => (
          <span
            key={lang.code}
            className={`translation-indicator ${blockContent[fieldName]?.[lang.code] ? "filled" : "empty"
              }`}
            title={`${lang.name}: ${blockContent[fieldName]?.[lang.code] ? "заполнено" : "пусто"
              }`}
          >
            {lang.code.toUpperCase()}
          </span>
        ))}
      </div>
    </div>
  );
};

// Простой компонент для полей без форматирования
const SimpleInput = ({
  fieldName,
  label,
  type = "text",
  required = false,
  blockContent,
  setBlockContent,
  currentLanguage,
  setCurrentLanguage,
  languages,
}) => {
  const currentValue = blockContent[fieldName]?.[currentLanguage] || "";

  const handleChange = (value) => {
    setBlockContent((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        [currentLanguage]: value,
      },
    }));
  };

  return (
    <div className="form-group">
      <label>{label}:</label>

      <div className="language-tabs">
        {languages.map((lang) => (
          <button
            key={lang.code}
            type="button"
            className={`language-tab ${currentLanguage === lang.code ? "active" : ""
              }`}
            onClick={() => setCurrentLanguage(lang.code)}
          >
            {lang.name}
          </button>
        ))}
      </div>

      <input
        type={type}
        value={currentValue}
        onChange={(e) => handleChange(e.target.value)}
        required={required}
        placeholder={`Введите ${label.toLowerCase()} на ${languages.find((l) => l.code === currentLanguage)?.name
          }`}
      />

      <div className="translation-indicators">
        {languages.map((lang) => (
          <span
            key={lang.code}
            className={`translation-indicator ${blockContent[fieldName]?.[lang.code] ? "filled" : "empty"
              }`}
            title={`${lang.name}: ${blockContent[fieldName]?.[lang.code] ? "заполнено" : "пусто"
              }`}
          >
            {lang.code.toUpperCase()}
          </span>
        ))}
      </div>
    </div>
  );
};

// Функция для получения текста на нужном языке для отображения
const getTextByLanguage = (textObj, preferredLang = "kz") => {
  if (!textObj || typeof textObj !== "object") return textObj || "";

  return (
    textObj[preferredLang] ||
    textObj.kz ||
    textObj.ru ||
    textObj.en ||
    Object.values(textObj)[0] ||
    ""
  );
};

const EditableBlock = ({
  block,
  onBlockUpdated,
  currentLanguage: displayLanguage = "kz",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(block.content || {});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentLanguage, setCurrentLanguage] = useState("kz");
  const { showNotification } = useNotification();

  const languages = [
    { code: "kz", name: "Қазақша" },
    { code: "ru", name: "Русский" },
    { code: "en", name: "English" },
  ];

  const handleEdit = () => {
    setEditContent(block.content || {});
    setIsEditing(true);
    setError("");
  };

  const handleCancel = () => {
    setEditContent(block.content || {});
    setIsEditing(false);
    setError("");
    setCurrentLanguage("kz");
  };

  const handleImageUploaded = (imageData) => {
    if (block.type === "ImageBlock") {
      const currentImages = editContent.images || [];
      setEditContent({
        ...editContent,
        images: [...currentImages, ...imageData],
      });
    } else if (block.type === "TextImageBlock") {
      setEditContent({
        ...editContent,
        imageUrl: imageData.imageUrl,
        altText: {
          ...editContent.altText,
          [currentLanguage]: imageData.altText || "",
        },
      });
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await apiPatch(
        `${process.env.REACT_APP_API_URL}/api/block/${block.id}`,
        {
          content: editContent,
        }
      );

      onBlockUpdated(response.data);
      setIsEditing(false);
      setCurrentLanguage("kz");
      showNotification("Блок успешно обновлен", "success");
    } catch (err) {
      setError(
        `Ошибка при обновлении: ${err.response?.data?.message || err.message}`
      );
      showNotification("Ошибка при обновлении блока", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderEditForm = () => {
    switch (block.type) {
      case "TitleBlock":
        return (
          <div className="edit-form">
            <EnhancedTextInput
              fieldName="title"
              label="Заголовок"
              required
              blockContent={editContent}
              setBlockContent={setEditContent}
              currentLanguage={currentLanguage}
              setCurrentLanguage={setCurrentLanguage}
              languages={languages}
              height={120}
            />
            <EnhancedTextInput
              fieldName="subtitle"
              label="Подзаголовок"
              blockContent={editContent}
              setBlockContent={setEditContent}
              currentLanguage={currentLanguage}
              setCurrentLanguage={setCurrentLanguage}
              languages={languages}
              height={100}
            />
          </div>
        );

      case "TextBlock":
        return (
          <div className="edit-form">
            <EnhancedTextInput
              fieldName="text"
              label="Текст"
              required
              blockContent={editContent}
              setBlockContent={setEditContent}
              currentLanguage={currentLanguage}
              setCurrentLanguage={setCurrentLanguage}
              languages={languages}
              height={200}
            />
          </div>
        );

      case "TextImageBlock":
        return (
          <div className="edit-form">
            <EnhancedTextInput
              fieldName="text"
              label="Текст"
              required
              blockContent={editContent}
              setBlockContent={setEditContent}
              currentLanguage={currentLanguage}
              setCurrentLanguage={setCurrentLanguage}
              languages={languages}
              height={180}
            />

            <div className="form-group">
              <label>Изображение:</label>
              <ImageUploader onImageUploaded={handleImageUploaded} />
              {editContent.imageUrl && (
                <div className="current-image">
                  <img
                    src={`${process.env.REACT_APP_API_URL}${editContent.imageUrl}`}
                    alt="Current image"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setEditContent({
                        ...editContent,
                        imageUrl: "",
                        altText: {},
                      })
                    }
                    className="remove-image-button"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Позиция изображения:</label>
              <select
                value={editContent.imagePosition || "right"}
                onChange={(e) =>
                  setEditContent({
                    ...editContent,
                    imagePosition: e.target.value,
                  })
                }
              >
                <option value="right">Справа</option>
                <option value="left">Слева</option>
                <option value="top">Сверху</option>
                <option value="bottom">Снизу</option>
              </select>
            </div>

            <SimpleInput
              fieldName="altText"
              label="Альтернативный текст для изображения"
              blockContent={editContent}
              setBlockContent={setEditContent}
              currentLanguage={currentLanguage}
              setCurrentLanguage={setCurrentLanguage}
              languages={languages}
            />
          </div>
        );

      case "ImageBlock":
        return (
          <div className="edit-form">
            <div className="form-group">
              <label>Изображения:</label>
              <ImageUploader
                onImageUploaded={handleImageUploaded}
                multiple={true}
              />
              {editContent.images && editContent.images.length > 0 && (
                <div className="current-images">
                  {editContent.images.map((image, index) => (
                    <div key={index} className="current-image">
                      <img
                        src={`${process.env.REACT_APP_API_URL}${image.imageUrl}`}
                        alt={`Image ${index + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updatedImages = editContent.images.filter(
                            (_, i) => i !== index
                          );
                          setEditContent({
                            ...editContent,
                            images: updatedImages,
                          });
                        }}
                        className="remove-image-button"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case "LinksBlock":
        return (
          <div className="edit-form">
            <EnhancedTextInput
              fieldName="title"
              label="Заголовок блока ссылок"
              required
              blockContent={editContent}
              setBlockContent={setEditContent}
              currentLanguage={currentLanguage}
              setCurrentLanguage={setCurrentLanguage}
              languages={languages}
              height={120}
            />

            <div className="form-group">
              <label>Стиль отображения:</label>
              <select
                value={editContent.displayStyle || "default"}
                onChange={(e) =>
                  setEditContent({
                    ...editContent,
                    displayStyle: e.target.value,
                  })
                }
              >
                <option value="default">Обычный список</option>
                <option value="table">Таблица</option>
              </select>
            </div>

            <div className="form-group">
              <label>Ссылки:</label>
              {!editContent.links || editContent.links.length === 0 ? (
                <p>Нет ссылок. Добавьте новую ссылку.</p>
              ) : (
                <div className="links-list">
                  {editContent.links.map((link, index) => (
                    <div key={index} className="link-item">
                      <div className="link-text-field">
                        <label>Текст ссылки:</label>
                        <div className="language-tabs-small">
                          {languages.map((lang) => (
                            <button
                              key={lang.code}
                              type="button"
                              className={`language-tab-small ${currentLanguage === lang.code ? "active" : ""
                                }`}
                              onClick={() => setCurrentLanguage(lang.code)}
                            >
                              {lang.code.toUpperCase()}
                            </button>
                          ))}
                        </div>
                        <input
                          type="text"
                          placeholder={`Текст ссылки на ${languages.find((l) => l.code === currentLanguage)
                              ?.name
                            }`}
                          value={link.text?.[currentLanguage] || ""}
                          onChange={(e) => {
                            const updatedLinks = [...editContent.links];
                            if (!updatedLinks[index].text) {
                              updatedLinks[index].text = {};
                            }
                            updatedLinks[index].text[currentLanguage] =
                              e.target.value;
                            setEditContent({
                              ...editContent,
                              links: updatedLinks,
                            });
                          }}
                        />
                      </div>

                      <div className="link-url-field">
                        <label>URL:</label>
                        <div className="language-tabs-small">
                          {languages.map((lang) => (
                            <button
                              key={lang.code}
                              type="button"
                              className={`language-tab-small ${currentLanguage === lang.code ? "active" : ""
                                }`}
                              onClick={() => setCurrentLanguage(lang.code)}
                            >
                              {lang.code.toUpperCase()}
                            </button>
                          ))}
                        </div>
                        <input
                          type="text"
                          placeholder={`URL на ${languages.find((l) => l.code === currentLanguage)
                              ?.name
                            }`}
                          value={link.url?.[currentLanguage] || ""}
                          onChange={(e) => {
                            const updatedLinks = [...editContent.links];
                            if (!updatedLinks[index].url) {
                              updatedLinks[index].url = {};
                            }
                            updatedLinks[index].url[currentLanguage] =
                              e.target.value;
                            setEditContent({
                              ...editContent,
                              links: updatedLinks,
                            });
                          }}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          const updatedLinks = editContent.links.filter(
                            (_, i) => i !== index
                          );
                          setEditContent({
                            ...editContent,
                            links: updatedLinks,
                          });
                        }}
                        className="remove-link-btn"
                      >
                        Удалить
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  const links = editContent.links || [];
                  setEditContent({
                    ...editContent,
                    links: [
                      ...links,
                      { text: { en: "", ru: "", kz: "" }, url: { en: "", ru: "", kz: "" } },
                    ],
                  });
                }}
                className="add-button-small"
              >
                Добавить ссылку
              </button>
            </div>
          </div>
        );

      default:
        return <div>Неподдерживаемый тип блока для редактирования</div>;
    }
  };

  const renderDisplayContent = () => {
    const HTMLContent = ({ content, className = "" }) => {
      const htmlContent = getTextByLanguage(content, displayLanguage);
      if (!htmlContent) return null;
      return (
        <div
          className={`html-content ${className}`}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      );
    };

    switch (block.type) {
      case "TitleBlock":
        return (
          <div className="title-block">
            <HTMLContent content={block.content.title} className="title-main" />
            {block.content.subtitle && (
              <HTMLContent
                content={block.content.subtitle}
                className="title-subtitle"
              />
            )}
          </div>
        );

      case "TextBlock":
        return (
          <div className="text-block">
            <HTMLContent
              content={block.content.text}
              className="text-content"
            />
          </div>
        );

      case "TextImageBlock":
        return (
          <div
            className={`text-image-block ${block.content.imagePosition || "right"
              }`}
          >
            <div className="text-content">
              <HTMLContent
                content={block.content.text}
                className="text-with-image"
              />
            </div>
            {block.content.imageUrl && (
              <div className="image-content">
                <img
                  src={`${process.env.REACT_APP_API_URL}${block.content.imageUrl}`}
                  alt={
                    getTextByLanguage(block.content.altText, displayLanguage) ||
                    "Image"
                  }
                />
              </div>
            )}
          </div>
        );

      case "ImageBlock":
        return (
          <div className="image-block">
            {block.content.images &&
              block.content.images.map((image, index) => (
                <div key={index} className="image-item">
                  <img
                    src={`${process.env.REACT_APP_API_URL}${image.imageUrl}`}
                    alt={image.altText || `Image ${index + 1}`}
                  />
                </div>
              ))}
          </div>
        );

      case "LinksBlock":
        const LinksDisplay = () => {
          const HTMLTitle = () => (
            <HTMLContent
              content={block.content.title}
              className="links-title"
            />
          );

          if (block.content.displayStyle === "table") {
            return (
              <div className="links-block table-style">
                <HTMLTitle />
                <div className="table-container">
                  <table className="links-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Ссылка</th>
                      </tr>
                    </thead>
                    <tbody>
                      {block.content.links &&
                        block.content.links.map((link, index) => (
                          <tr
                            key={index}
                            onClick={() => window.open(getTextByLanguage(link.url, displayLanguage), "_blank")}
                            className="clickable-row"
                          >
                            <td>{index + 1}</td>
                            <td>
                              {getTextByLanguage(link.text, displayLanguage)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          }

          if (block.content.displayStyle === "cards") {
            return (
              <div className="links-block cards-style">
                <HTMLTitle />
                <div className="links-cards">
                  {block.content.links &&
                    block.content.links.map((link, index) => (
                      <a
                        key={index}
                        href={getTextByLanguage(link.url, displayLanguage)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-card"
                      >
                        <span className="card-number">{index + 1}</span>
                        <span className="card-text">
                          {getTextByLanguage(link.text, displayLanguage)}
                        </span>
                        <span className="card-arrow">→</span>
                      </a>
                    ))}
                </div>
              </div>
            );
          }

          if (block.content.displayStyle === "buttons") {
            return (
              <div className="links-block buttons-style">
                <HTMLTitle />
                <div className="links-buttons">
                  {block.content.links &&
                    block.content.links.map((link, index) => (
                      <a
                        key={index}
                        href={getTextByLanguage(link.url, displayLanguage)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-button"
                      >
                        {getTextByLanguage(link.text, displayLanguage)}
                      </a>
                    ))}
                </div>
              </div>
            );
          }

          return (
            <div className="links-block default-style">
              <HTMLTitle />
              <ul className="links-list">
                {block.content.links &&
                  block.content.links.map((link, index) => (
                    <li key={index} className="link-item">
                      <a
                        href={getTextByLanguage(link.url, displayLanguage)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="link-text">
                          {getTextByLanguage(link.text, displayLanguage)}
                        </span>
                        <span className="link-icon">↗</span>
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          );
        };

        return <LinksDisplay />;

      default:
        return <div>Неподдерживаемый тип блока</div>;
    }
  };

  if (isEditing) {
    return (
      <div className="editable-block editing">
        {error && <div className="error-message">{error}</div>}
        {renderEditForm()}
        <div className="edit-actions">
          <button
            onClick={handleSave}
            disabled={loading}
            className="save-button"
          >
            {loading ? "Сохранение..." : "Сохранить"}
          </button>
          <button
            onClick={handleCancel}
            disabled={loading}
            className="cancel-button"
          >
            Отмена
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="editable-block">
      <div className="block-content" onClick={handleEdit}>
        {renderDisplayContent()}
      </div>
    </div>
  );
};

export default EditableBlock;
